"use client"

import { usePathname } from "next/navigation"
import type { PostHog } from "posthog-js"
import { useCallback, useEffect, useRef, useState } from "react"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { getPostHogConsentFromDocument } from "@/lib/posthog-consent"
import {
  enrichExceptionEvent,
  installResizeObserverDiagnostics,
} from "@/lib/resize-observer-diagnostics"
import { isRunningInE2E } from "@/lib/utils"

import usePostHogConsent from "@/hooks/use-posthog-consent"
import type { User } from "@/nango/types/nango.user/user/User"

// The only areas PostHog runs in. Everything outside them (landing, blog,
// pricing, /login) never loads posthog-js at all, so an anonymous visitor pays
// nothing for analytics they are not part of.
// - /logged-in also covers the onboarding wizard, which is a modal rendered
//   inside it rather than a route of its own.
// - /sample is public, but it is the last step of the lead-magnet funnel and
//   captures lead_magnet_campaign_viewed, so it needs a live client.
const APP_PATH_PREFIXES = ["/logged-in", "/sample"]
// Upper bound on how long the idle scheduler may hold PostHog back.
const POSTHOG_INIT_IDLE_TIMEOUT_MS = 3000
// Fallback delay for browsers without requestIdleCallback (Safari).
const POSTHOG_INIT_FALLBACK_MS = 1500
// Dead clicks, replay, surveys, web vitals and exception autocapture are not in
// the posthog-js bundle: each one fetches its own script from the PostHog CDN on
// init. Locally those fetches fail (ad blockers, offline work) and every failure
// logs a console error, so the CDN-backed extras stay off outside production.
// Event capture, pageviews, autocapture and feature flags are untouched.
const skipCdnLoadedFeatures = process.env.NODE_ENV !== "production"

function isProductPath(pathname: string): boolean {
  return APP_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/**
 * Run after the browser is done with the initial render. posthog-js is ~60 KB
 * before it fetches the recorder, surveys and web-vitals bundles on top, and
 * loading any of it during hydration competes with painting the page.
 */
function runWhenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(callback, {
      timeout: POSTHOG_INIT_IDLE_TIMEOUT_MS,
    })
    return () => window.cancelIdleCallback(id)
  }
  const id = window.setTimeout(callback, POSTHOG_INIT_FALLBACK_MS)
  return () => window.clearTimeout(id)
}

export function PostHogProvider({
  children,
  hasSession,
}: {
  children: React.ReactNode
  /** Whether the visitor arrived with auth cookies, read server-side. */
  hasSession: boolean
}) {
  const { consent, isMounted } = usePostHogConsent()
  const hasHandledInitialConsentSync = useRef(false)
  const [client, setClient] = useState<PostHog | null>(null)
  const pathname = usePathname() || "/"
  const onProductPage = isProductPath(pathname)

  const identifyCurrentUser = useCallback(
    async (posthog: PostHog) => {
      // A signed-out visitor has no session, and asking anyway produces a 401
      // on /api/user/user/ plus a second one on the refresh retry.
      if (!hasSession) return
      try {
        const user = await apiClient<User>({
          url: apiUrl("api/user/user/"),
          method: "GET",
        })
        posthog.identify(String(user.id), {
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
        })
      } catch {
        if (posthog._isIdentified()) {
          posthog.reset()
        }
      }
    },
    [hasSession],
  )

  // A ResizeObserver delivery loop reaches PostHog as a stackless synthetic
  // error, with nothing naming the observer behind it. Wrapping the
  // constructor here records that link ourselves. It runs outside
  // `runWhenIdle` on purpose: the page builds its observers during
  // hydration, long before the idle callback that loads posthog-js fires,
  // and an observer created before the wrapper is invisible to it.
  useEffect(() => {
    if (!onProductPage) return
    return installResizeObserverDiagnostics(window)
  }, [onProductPage])

  useEffect(() => {
    if (client) return
    // Outside the product, skip the dynamic import entirely rather than
    // initialising a trimmed client. A visitor who navigates in from a public
    // page flips onProductPage, which re-runs this effect and loads PostHog then.
    if (!onProductPage) return

    let cancelled = false
    const cancelIdle = runWhenIdle(() => {
      void (async () => {
        const { default: posthog } = await import("posthog-js")
        if (cancelled) return
        setClient(posthog)
        if (posthog.__loaded) return

        const inE2E = isRunningInE2E()
        const skipCdnFeatures = inE2E || skipCdnLoadedFeatures
        const initialConsent = getPostHogConsentFromDocument()

        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_TOKEN as string, {
          api_host: "https://eu.i.posthog.com",
          ui_host: "https://eu.posthog.com",
          defaults: "2026-01-30",
          capture_exceptions: !skipCdnFeatures,
          before_send: enrichExceptionEvent,
          autocapture: !inE2E,
          capture_pageview: !inE2E,
          capture_pageleave: !inE2E,
          // Replay, surveys, dead clicks and web vitals pull ~110 KB of extra
          // script, which is wasted work in a Cypress run and unreachable in dev.
          disable_session_recording: skipCdnFeatures,
          disable_surveys: skipCdnFeatures,
          capture_dead_clicks: !skipCdnFeatures,
          // Heatmaps stay in the main bundle but spin up their own dead-clicks
          // detector with an always-on flag, so leaving them to the project
          // setting re-fetches the CDN script that capture_dead_clicks: false
          // was meant to skip. Off outside production, server-driven inside it.
          capture_heatmaps: skipCdnFeatures ? false : undefined,
          capture_performance: skipCdnFeatures ? false : undefined,
          advanced_disable_flags: inE2E,
          advanced_disable_feature_flags: inE2E,
          debug: process.env.NODE_ENV === "development",
          opt_out_capturing_by_default: inE2E || initialConsent === "denied",
        })

        posthog.register({ source: "client_next" })

        if (inE2E) {
          posthog.opt_out_capturing()
          return
        }

        if (initialConsent === "denied") {
          posthog.opt_out_capturing()
          posthog.reset()
          return
        }

        void identifyCurrentUser(posthog)
      })()
    })

    return () => {
      cancelled = true
      cancelIdle()
    }
  }, [client, identifyCurrentUser, onProductPage])

  useEffect(() => {
    if (!isMounted || !client) {
      return
    }

    if (!hasHandledInitialConsentSync.current) {
      hasHandledInitialConsentSync.current = true
      return
    }

    if (consent === "denied") {
      client.opt_out_capturing()
      client.reset()
      return
    }

    client.opt_in_capturing()
    void identifyCurrentUser(client)
  }, [client, consent, identifyCurrentUser, isMounted])

  return <>{children}</>
}
